import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Autocomplete,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Calculate as CalcIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  LocalOffer as LocalOfferIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
  Rule as RuleIcon,
  HealthAndSafety as HealthIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../../components/CronBuilderBox";
import { useTheme } from "@mui/material/styles";
import {
  getFilterPanelSx,
  getTableHeadRowSx,
  getDetailPanelSx,
} from "../../../../theme/panelStyles";

import Draggable from "react-draggable";
import { RepricerArticlesApi } from "../../../../services/api/multi/repricer";
import { HealthApi } from "../../../../services/api/multi/health";
import { formatPricingHistory } from "../../utils/pricingHistory";
import { OttoMarginRulesApi } from "../../../../services/api/multi/otto";
import { ShopifyApi } from "../../../../services/api/multi/shopify";
import {
  ArticleSalesStatsDialog,
  SalesStatsOpenButton,
} from "../../components/ArticleSalesStatsDialog";

// -- cache helpers ------------------------------------------------------------

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

function getCached(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCached(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // storage quota exceeded — ignore
  }
}

// -- Quick View Dialog --------------------------------------------------------

function DialogDraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-quickview-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function SkuQuickViewDialog({ open, onClose, sku, onOpenSku }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !sku) {
      setData(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);

    RepricerArticlesApi.getArticle(sku)
      .then((res) => {
        if (active) {
          setData(res.data ? res.data : res);
          setError(null);
        }
      })
      .catch((err) => {
        if (active) {
          console.error("Failed to load article details:", err);
          setError(
            err.response?.data?.detail
              ? JSON.stringify(err.response.data.detail)
              : err.message,
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, sku]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      hideBackdrop
      disableScrollLock
      disableEnforceFocus
      disableAutoFocus
      PaperComponent={DialogDraggablePaper}
      PaperProps={{
        sx: {
          m: 2,
          pointerEvents: "auto",
          maxHeight: "80vh",
          boxShadow: 24,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.300",
        },
      }}
      sx={{
        pointerEvents: "none",
        zIndex: 1300,
      }}
      aria-labelledby="draggable-quickview-title"
    >
      <DialogTitle
        id="draggable-quickview-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          pt: 1.5,
          cursor: "move",
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "grey.300",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
          Article Quick View: {sku}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "grey.500" }}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: "grey.50" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && data && (
          <ArticleExpandedDetails
            article={data}
            onOpenSku={onOpenSku}
            inQuickView
          />
        )}
        {!loading && !error && !data && (
          <Typography sx={{ p: 3 }} color="text.secondary">
            No base details found or API returned empty for {sku}.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -- helpers ------------------------------------------------------------------

const PLATFORM_CONFIG = [
  {
    key: "shopify",
    label: "S",
    title: "Shopify",
    color: "#96bf48",
    navKey: "shopify",
  },
  {
    key: "kaufland",
    label: "K",
    title: "Kaufland",
    color: "#e10915",
    navKey: "kaufland",
  },
  { key: "otto", label: "O", title: "Otto", color: "#f00020", navKey: "otto" },
  { key: "fbm", label: "F", title: "FBM", color: "#1976d2", navKey: "fbm" },
  { key: "fba", label: "A", title: "FBA", color: "#ff9900", navKey: "fba" },
];

const fmt = (val, decimals = 2) =>
  val != null ? Number(val).toFixed(decimals) : null;

/** API returns FBM as an array (multi seller_sku); older payloads may be an object. */
function normalizeFbmList(fbm) {
  if (!fbm) return [];
  return Array.isArray(fbm) ? fbm : [fbm];
}

function pickPrimaryFbm(fbm, articleSku) {
  const list = normalizeFbmList(fbm);
  if (list.length === 0) return null;
  return (
    list.find((f) => f.seller_sku === articleSku && f.status !== "error") ||
    list.find((f) => f.seller_sku === articleSku) ||
    list.find((f) => f.status !== "error") ||
    list[0]
  );
}

function fbmDeBoundPrices(fbmDetail) {
  const pricing = fbmDetail?.international_pricing;
  const de = pricing?.DE || pricing?.de || null;
  return {
    min: de?.min_bound?.target_price ?? null,
    max: de?.max_bound?.target_price ?? null,
  };
}

// Shows which platform details are configured with optional click-to-navigate
function PlatformsIndicator({ article, onNavigate }) {
  return (
    <Stack direction="row" spacing={0.5}>
      {PLATFORM_CONFIG.map(({ key, label, title, color, navKey }) => {
        const exists =
          key === "fbm"
            ? normalizeFbmList(article.fbm).length > 0
            : !!article[key];
        const clickable = exists && navKey;
        return (
          <Tooltip
            key={key}
            title={
              exists
                ? navKey
                  ? `${title}: click to open`
                  : `${title}: configured`
                : `${title}: not set`
            }
          >
            <Chip
              label={label}
              size="small"
              onClick={
                clickable
                  ? (e) => {
                      e.stopPropagation();
                      onNavigate(navKey, article.sku);
                    }
                  : undefined
              }
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 700,
                minWidth: 20,
                px: 0.25,
                bgcolor: exists ? color : "grey.200",
                color: exists ? "#fff" : "grey.500",
                border: "none",
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { opacity: 0.85 } : {},
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      <Chip
        label={`Total: ${stats.total_count}`}
        color="primary"
        size="small"
      />
      <Chip
        label={`Stock: ${stats.total_stock}`}
        color="success"
        size="small"
      />
      {stats.total_stock_value != null && (
        <Chip label={`Value: ${fmt(stats.total_stock_value)} €`} size="small" />
      )}
    </Stack>
  );
}

// -- Inline detail row ---------------------------------------------------------

const DetailRow = ({ label, value }) => (
  <Grid container sx={{ py: 0.5 }} alignItems="flex-start">
    <Grid item xs={6}>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
    </Grid>
    <Grid item xs={6}>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography
          variant="caption"
          fontWeight={500}
          sx={{ wordBreak: "break-word" }}
        >
          {value ?? "-"}
        </Typography>
      )}
    </Grid>
  </Grid>
);

function BundleComponentsBreakdown({ bundlePkgs, onOpenSku, totalBuyPrice }) {
  const [loading, setLoading] = useState(false);
  const [componentsData, setComponentsData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!bundlePkgs || bundlePkgs.length === 0) return;

    setLoading(true);
    setError(null);

    Promise.all(
      bundlePkgs.map((pkg) =>
        RepricerArticlesApi.getArticle(pkg.sku)
          .then((res) => ({
            sku: pkg.sku,
            quantity: pkg.quantity,
            buy_price: res.data?.brutto_buy_price || res?.brutto_buy_price || 0,
            valid: true,
          }))
          .catch(() => ({
            sku: pkg.sku,
            quantity: pkg.quantity,
            buy_price: 0,
            valid: false,
          })),
      ),
    )
      .then((data) => {
        if (active) setComponentsData(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [bundlePkgs]);

  if (loading) {
    return <CircularProgress size={16} />;
  }

  if (error) {
    return (
      <Typography color="error" variant="caption">
        {error}
      </Typography>
    );
  }

  const calculatedTotal = componentsData.reduce(
    (acc, curr) => acc + curr.quantity * curr.buy_price,
    0,
  );

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack spacing={1}>
        {componentsData.map((c) => (
          <Box
            key={c.sku}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 0.75,
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {c.quantity}x
              </Typography>
              <Chip
                label={c.sku}
                size="small"
                color="secondary"
                variant="outlined"
                onClick={() => onOpenSku && onOpenSku(c.sku)}
                sx={{
                  cursor: "pointer",
                  height: 20,
                  fontSize: "0.65rem",
                }}
              />
            </Box>

            <Typography variant="caption" fontFamily="monospace">
              {c.valid ? (
                <>
                  {fmt(c.buy_price)} € ={" "}
                  <strong>{fmt(c.quantity * c.buy_price)} €</strong>
                </>
              ) : (
                <span style={{ color: "red" }}>N/A</span>
              )}
            </Typography>
          </Box>
        ))}

        {componentsData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pt: 0.5,
              borderTop: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Calculated Bundle Sum:
            </Typography>
            <Typography variant="caption" fontWeight={700}>
              {fmt(calculatedTotal)} €
            </Typography>
          </Box>
        )}

        {totalBuyPrice != null &&
          Math.abs(calculatedTotal - totalBuyPrice) > 0.01 && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: "block" }}
            >
              Note: Current assigned buy price is {fmt(totalBuyPrice)} €
            </Typography>
          )}
      </Stack>
    </Box>
  );
}

function ArticleExpandedDetails({ article, onOpenSku, inQuickView }) {
  const theme = useTheme();
  const childSkus = (article.child_skus || "")
    .split(",")
    .filter((s) => s.trim());
  let bundlePkgs = [];
  if (article.bundle_packages) {
    try {
      bundlePkgs =
        typeof article.bundle_packages === "string"
          ? JSON.parse(article.bundle_packages)
          : article.bundle_packages;
    } catch {}
  }
  const fbmDetail = pickPrimaryFbm(article.fbm, article.sku);
  const { min: fbmMin, max: fbmMax } = fbmDeBoundPrices(fbmDetail);

  return (
    <Box
      sx={{
        ...(inQuickView
          ? { px: 2, py: 1.5, bgcolor: "transparent" }
          : getDetailPanelSx(theme)),
      }}
    >
      <Grid container spacing={2}>
        {/* Base info */}
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Base
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          <DetailRow
            label="Plenty Article ID"
            value={article.plenty_article_id}
          />
          <DetailRow
            label="Plenty Variant ID"
            value={article.plenty_variant_id}
          />
          <DetailRow label="Parent SKU" value={article.parent_sku} />
          {childSkus.length > 0 && (
            <DetailRow
              label="Child SKUs"
              value={
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {childSkus.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={() => onOpenSku && onOpenSku(s)}
                      sx={{
                        cursor: "pointer",
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                  ))}
                </Stack>
              }
            />
          )}

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="Manufacturer" value={article.manufacturer} />
          <DetailRow label="EANs" value={article.eans?.join(", ")} />
          <DetailRow label="Weight (g)" value={article.weight} />
          <DetailRow label="Package" value={article.package_type} />
          <DetailRow
            label="Package Cost"
            value={
              article.package_cost != null
                ? `${fmt(article.package_cost)} €`
                : null
            }
          />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow
            label="Buy Price"
            value={
              article.brutto_buy_price != null ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Typography variant="caption" fontWeight={500}>
                    {fmt(article.brutto_buy_price)} €
                  </Typography>
                  {bundlePkgs.length > 0 && (
                    <Tooltip title="Sum of bundle components">
                      <Chip
                        label={`Bundle Sum (${bundlePkgs.reduce(
                          (acc, p) => acc + (p.quantity || 1),
                          0,
                        )} items)`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.6rem" }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              ) : null
            }
          />
          <DetailRow label="Bundle Type" value={article.bundle_type} />
          {bundlePkgs.length > 0 && (
            <DetailRow
              label="Bundle Components"
              value={
                <BundleComponentsBreakdown
                  bundlePkgs={bundlePkgs}
                  totalBuyPrice={article.brutto_buy_price}
                  onOpenSku={onOpenSku}
                />
              }
            />
          )}

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow
            label="Last Inbound Date"
            value={article.last_inbound_date}
          />
          <DetailRow label="Article Type" value={article.article_type} />
          <DetailRow
            label="Pricing History"
            value={formatPricingHistory(article.pricing_history)}
          />
          <DetailRow
            label="Ratios (Last 10)"
            value={
              article.ratios?.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {article.ratios.slice(0, 10).map((r, i) => (
                    <Chip
                      key={i}
                      label={typeof r === "number" ? r.toFixed(2) : r}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.6rem" }}
                    />
                  ))}
                </Stack>
              ) : null
            }
          />
        </Grid>

        {article.shopify && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Shopify
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Price"
              value={
                article.shopify.price != null
                  ? `${fmt(article.shopify.price)} €`
                  : null
              }
            />
            <DetailRow
              label="Min Price"
              value={
                article.shopify.minimum_price != null
                  ? `${fmt(article.shopify.minimum_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Max Price"
              value={
                article.shopify.maximum_price != null
                  ? `${fmt(article.shopify.maximum_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Expected Profit"
              value={
                article.shopify.expected_profit != null
                  ? `${fmt(article.shopify.expected_profit)} €`
                  : null
              }
            />
            <DetailRow label="Status" value={article.shopify.status} />
            {article.shopify.error_message && (
              <DetailRow label="Error" value={article.shopify.error_message} />
            )}
          </Grid>
        )}

        {article.kaufland && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Kaufland
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Price"
              value={
                article.kaufland.price != null
                  ? `${fmt(article.kaufland.price)} €`
                  : null
              }
            />
            <DetailRow
              label="Listing Price"
              value={
                article.kaufland.listing_price != null
                  ? `${fmt(article.kaufland.listing_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Min Price"
              value={
                article.kaufland.minimum_price != null
                  ? `${fmt(article.kaufland.minimum_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Max Price"
              value={
                article.kaufland.maximum_price != null
                  ? `${fmt(article.kaufland.maximum_price)} €`
                  : null
              }
            />
            <DetailRow label="Storefront" value={article.kaufland.storefront} />
            <DetailRow
              label="Buybox"
              value={
                article.kaufland.is_buybox != null
                  ? article.kaufland.is_buybox
                    ? "Yes"
                    : "No"
                  : null
              }
            />
            <DetailRow
              label="Expected Profit"
              value={
                article.kaufland.expected_profit != null
                  ? `${fmt(article.kaufland.expected_profit)} €`
                  : null
              }
            />
            <DetailRow label="Status" value={article.kaufland.status} />
            {article.kaufland.error_message && (
              <DetailRow label="Error" value={article.kaufland.error_message} />
            )}
          </Grid>
        )}

        {article.otto && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Otto
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Sale Price"
              value={
                article.otto.sale_price != null
                  ? `${fmt(article.otto.sale_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Price"
              value={
                article.otto.price != null
                  ? `${fmt(article.otto.price)} €`
                  : null
              }
            />
            <DetailRow
              label="Site Price"
              value={
                article.otto.site_price != null
                  ? `${fmt(article.otto.site_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Min Price"
              value={
                article.otto.min_price != null
                  ? `${fmt(article.otto.min_price)} €`
                  : null
              }
            />
            <DetailRow
              label="Max Price"
              value={
                article.otto.max_price != null
                  ? `${fmt(article.otto.max_price)} €`
                  : null
              }
            />
            <DetailRow
              label="In Buybox"
              value={
                article.otto.is_in_buybox != null
                  ? article.otto.is_in_buybox
                    ? "Yes"
                    : "No"
                  : null
              }
            />
            <DetailRow label="Seller" value={article.otto.seller} />
            <DetailRow
              label="Expected Profit"
              value={
                article.otto.expected_profit != null
                  ? `${fmt(article.otto.expected_profit)} €`
                  : null
              }
            />
            <DetailRow label="Status" value={article.otto.status} />
            {article.otto.error_message && (
              <DetailRow label="Error" value={article.otto.error_message} />
            )}
          </Grid>
        )}

        {fbmDetail && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              FBM
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow
              label="Price"
              value={fbmMax != null ? `${fmt(fbmMax)} €` : null}
            />
            <DetailRow
              label="Min Price"
              value={fbmMin != null ? `${fmt(fbmMin)} €` : null}
            />
            <DetailRow
              label="Max Price"
              value={fbmMax != null ? `${fmt(fbmMax)} €` : null}
            />
            <DetailRow
              label="Expected Profit"
              value={
                fbmDetail.expected_profit != null
                  ? `${fmt(fbmDetail.expected_profit)} €`
                  : null
              }
            />
            <DetailRow label="Status" value={fbmDetail.status} />
            {fbmDetail.error_message && (
              <DetailRow label="Error" value={fbmDetail.error_message} />
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// -- Edit article dialog ------------------------------------------------------

const EMPTY_FORM = {
  articlename: "",
  brutto_buy_price: "",
  stock: "",
  weight: "",
  package_type: "",
  package_cost: "",
  last_inbound_date: "",
  article_type: "",
  min_uvp: "",
  max_uvp: "",
  eans: "",
  plenty_article_id: "",
  plenty_variant_id: "",
};

function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    // already YYYY-MM-DD
    return String(value).slice(0, 10);
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function EditArticleDialog({ article, open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!article) return;
    setForm({
      articlename: article.articlename ?? "",
      brutto_buy_price: article.brutto_buy_price ?? "",
      stock: article.stock ?? "",
      weight: article.weight ?? "",
      package_type: article.package_type ?? "",
      package_cost: article.package_cost ?? "",
      last_inbound_date: toDateInputValue(article.last_inbound_date),
      article_type: article.article_type ?? "",
      min_uvp: article.min_uvp ?? "",
      max_uvp: article.max_uvp ?? "",
      eans: Array.isArray(article.eans) ? article.eans.join(", ") : "",
      plenty_article_id: article.plenty_article_id ?? "",
      plenty_variant_id: article.plenty_variant_id ?? "",
    });
    setError(null);
  }, [article]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      if (form.articlename !== (article.articlename ?? ""))
        payload.articlename = form.articlename || null;
      if (
        String(form.brutto_buy_price) !== String(article.brutto_buy_price ?? "")
      )
        payload.brutto_buy_price =
          form.brutto_buy_price !== "" ? Number(form.brutto_buy_price) : null;
      if (String(form.stock) !== String(article.stock ?? ""))
        payload.stock = form.stock !== "" ? Number(form.stock) : null;
      if (String(form.weight) !== String(article.weight ?? ""))
        payload.weight = form.weight !== "" ? Number(form.weight) : null;
      if (form.package_type !== (article.package_type ?? ""))
        payload.package_type = form.package_type || null;
      if (String(form.package_cost) !== String(article.package_cost ?? ""))
        payload.package_cost =
          form.package_cost !== "" ? Number(form.package_cost) : null;

      const currentInbound = toDateInputValue(article.last_inbound_date);
      if (form.last_inbound_date !== currentInbound) {
        payload.last_inbound_date = form.last_inbound_date
          ? `${form.last_inbound_date}T00:00:00`
          : null;
      }

      if ((form.article_type || "") !== (article.article_type ?? "")) {
        payload.article_type = form.article_type || null;
      }

      if (String(form.min_uvp) !== String(article.min_uvp ?? "")) {
        payload.min_uvp = form.min_uvp !== "" ? Number(form.min_uvp) : null;
      }
      if (String(form.max_uvp) !== String(article.max_uvp ?? "")) {
        payload.max_uvp = form.max_uvp !== "" ? Number(form.max_uvp) : null;
      }

      const currentEans = Array.isArray(article.eans)
        ? article.eans.join(", ")
        : "";
      if (form.eans !== currentEans) {
        payload.eans = form.eans
          ? form.eans
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : [];
      }

      if (
        String(form.plenty_article_id) !==
        String(article.plenty_article_id ?? "")
      ) {
        payload.plenty_article_id =
          form.plenty_article_id !== "" ? Number(form.plenty_article_id) : null;
      }
      if (
        String(form.plenty_variant_id) !==
        String(article.plenty_variant_id ?? "")
      ) {
        payload.plenty_variant_id =
          form.plenty_variant_id !== "" ? Number(form.plenty_variant_id) : null;
      }

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await RepricerArticlesApi.updateArticle(article.sku, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const f = (label, field, type = "text", helperText) => (
    <TextField
      label={label}
      value={form[field]}
      onChange={handleChange(field)}
      type={type}
      size="small"
      fullWidth
      helperText={helperText}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      inputProps={{ step: "any" }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Article: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === "string" ? error : JSON.stringify(error)}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            {f("Name", "articlename")}
          </Grid>
          <Grid item xs={6}>
            {f("Buy Price (€) (brutto)", "brutto_buy_price", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Stock", "stock", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Weight (g)", "weight", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Package Cost (€)", "package_cost", "number")}
          </Grid>
          <Grid item xs={12}>
            {f("Package Type", "package_type")}
          </Grid>
          <Grid item xs={6}>
            {f(
              "Last Inbound Date",
              "last_inbound_date",
              "date",
              "Used for inventory aging",
            )}
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="edit-article-type-label">Article Type</InputLabel>
              <Select
                labelId="edit-article-type-label"
                label="Article Type"
                value={form.article_type}
                onChange={handleChange("article_type")}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="penner">penner</MenuItem>
                <MenuItem value="renner">renner</MenuItem>
                <MenuItem value="neutral">neutral</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            {f("Min UVP (€)", "min_uvp", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Max UVP (€)", "max_uvp", "number")}
          </Grid>
          <Grid item xs={12}>
            {f("EANs", "eans", "text", "Comma-separated")}
          </Grid>
          <Grid item xs={6}>
            {f("Plenty Article ID", "plenty_article_id", "number")}
          </Grid>
          <Grid item xs={6}>
            {f("Plenty Variant ID", "plenty_variant_id", "number")}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Delete confirmation dialog ------------------------------------------------

function DeleteConfirmDialog({ sku, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await RepricerArticlesApi.deleteArticle(sku);
      onDeleted();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Article</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Typography>
          Are you sure you want to delete article <strong>{sku}</strong>? This
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Cron dialog ---------------------------------------------------------------

function CronDialog({
  open,
  value,
  onChange,
  onConfirm,
  onClose,
  updatePrices,
  onUpdatePricesChange,
  discordWebhook,
  onDiscordWebhookChange,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Cron Schedule</DialogTitle>
      <DialogContent>
        <CronBuilderBox>
          <Cron value={value} showResultText onChange={onChange} />
        </CronBuilderBox>
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Tooltip title="If checked, the cron job will also recalculate and push prices to the connected platforms for all affected articles.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={updatePrices}
                  onChange={(e) => onUpdatePricesChange(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Update Prices on Remote Platforms
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Tooltip>
          <TextField
            label="Discord Webhook URL (Optional)"
            size="small"
            fullWidth
            value={discordWebhook}
            onChange={(e) => onDiscordWebhookChange(e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
            variant="outlined"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          Save & Start
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Health audit dialog ------------------------------------------------------

function HealthAuditDialog({
  open,
  onClose,
  initialConfig,
  initialReport,
  onSaved,
}) {
  const [days, setDays] = useState(initialConfig?.days ?? 3);
  const [maxHours, setMaxHours] = useState(
    initialConfig?.max_hours_after_base_run ?? 48,
  );
  const [externalVerifyLimit, setExternalVerifyLimit] = useState(
    initialConfig?.external_verify_limit ?? 0,
  );
  const [webhook, setWebhook] = useState(initialConfig?.discord_webhook ?? "");
  const [notify, setNotify] = useState(
    initialConfig?.notify_on_nonzero ?? true,
  );
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(initialReport || null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDays(initialConfig?.days ?? 3);
    setMaxHours(initialConfig?.max_hours_after_base_run ?? 48);
    setExternalVerifyLimit(initialConfig?.external_verify_limit ?? 0);
    setWebhook(initialConfig?.discord_webhook ?? "");
    setNotify(initialConfig?.notify_on_nonzero ?? true);
    setReport(initialReport || null);
    setErr(null);
  }, [open, initialConfig, initialReport]);

  const saveConfig = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await HealthApi.updateCronConfig({
        days,
        max_hours_after_base_run: maxHours,
        external_verify_limit: externalVerifyLimit,
        discord_webhook: webhook,
        notify_on_nonzero: notify,
      });
      onSaved?.(res.data);
    } catch (e) {
      setErr(
        e?.response?.data?.detail || e?.message || "Failed to save config",
      );
    } finally {
      setSaving(false);
    }
  };

  const runAudit = async () => {
    setRunning(true);
    setErr(null);
    try {
      const res = await HealthApi.runBuyPriceAudit({
        days,
        max_hours_after_base_run: maxHours,
        external_verify_limit: externalVerifyLimit,
        limit: 50,
      });
      setReport(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Audit failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Health Audit</DialogTitle>
      <DialogContent dividers>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Window (days)"
              value={days}
              inputProps={{ min: 1, max: 30 }}
              onChange={(e) => setDays(Number(e.target.value))}
              helperText="Checks latest shopping_table EK rows in this window."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Max hours after base run"
              value={maxHours}
              inputProps={{ min: 1, max: 168 }}
              onChange={(e) => setMaxHours(Number(e.target.value))}
              helperText="Base cron is daily; newer EK is pending until next run."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="External API verify limit"
              value={externalVerifyLimit}
              inputProps={{ min: 0, max: 100000 }}
              onChange={(e) => setExternalVerifyLimit(Number(e.target.value))}
              helperText="0 = check every article against Kaufland/Sellerlogic (rate-limited). Positive = sample size."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
              }
              label="Send Discord summary when audit finishes"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Discord webhook (optional)"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder="https://discord.com/api/webhooks/..."
              helperText="Posts a finish summary (counts + samples) after each audit."
            />
          </Grid>
        </Grid>

        {report && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Latest Report
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} md={3}>
                <Chip
                  label={`checked: ${report.checked ?? 0}`}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Chip
                  color={
                    (report.missing_local_count ?? 0) > 0 ? "error" : "default"
                  }
                  label={`missing local: ${report.missing_local_count ?? 0}`}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Chip
                  color={
                    (report.buy_price_mismatch_count ?? 0) > 0
                      ? "error"
                      : "default"
                  }
                  label={`buy price mismatch: ${
                    report.buy_price_mismatch_count ?? 0
                  }`}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Chip
                  color={
                    (report.fba_stuck_update_needed_count ?? 0) > 0
                      ? "warning"
                      : "default"
                  }
                  label={`FBA stuck: ${
                    report.fba_stuck_update_needed_count ?? 0
                  }`}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Chip
                  color={
                    (report.stock_mismatch_count ?? 0) > 0 ? "error" : "default"
                  }
                  label={`stock mismatch: ${report.stock_mismatch_count ?? 0}`}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Chip
                  color={
                    (report.external_bound_mismatch_count ?? 0) > 0
                      ? "error"
                      : "default"
                  }
                  label={`external mismatch: ${
                    report.external_bound_mismatch_count ?? 0
                  }`}
                  variant="outlined"
                />
              </Grid>
              {(report.external_verify_channels_checked ?? 0) > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Live API checks: {report.external_verify_channels_checked}{" "}
                    channel bounds verified via Kaufland / Sellerlogic (
                    {report.external_verify_candidates ?? 0} articles
                    {report.external_verify_mode === "all"
                      ? ", full catalog"
                      : ""}
                    ).
                  </Typography>
                </Grid>
              )}
            </Grid>

            {report.generated_at && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                generated_at: {new Date(report.generated_at).toLocaleString()}
                {report.eval_filename ? ` · file: ${report.eval_filename}` : ""}
              </Typography>
            )}

            {(report.missing_local?.length > 0 ||
              report.buy_price_mismatch?.length > 0 ||
              report.fba_stuck_update_needed?.length > 0 ||
              report.stock_mismatch?.length > 0 ||
              report.external_bound_mismatch?.length > 0) && (
              <Box sx={{ mt: 2 }}>
                {report.missing_local?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Missing local ({report.missing_local_count})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ASIN/SKU</TableCell>
                            <TableCell>Buy price</TableCell>
                            <TableCell>Datum</TableCell>
                            <TableCell>Linked base</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.missing_local.map((row) => (
                            <TableRow key={row.asin_sku}>
                              <TableCell>{row.asin_sku}</TableCell>
                              <TableCell>{row.buy_price ?? "—"}</TableCell>
                              <TableCell>
                                {row.datum
                                  ? new Date(row.datum).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {row.linked_base_sku ?? "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {report.buy_price_mismatch?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Buy price mismatch ({report.buy_price_mismatch_count})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>SKU</TableCell>
                            <TableCell>Shopping EK</TableCell>
                            <TableCell>Local EK</TableCell>
                            <TableCell>Shopping datum</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.buy_price_mismatch.map((row) => (
                            <TableRow key={row.sku}>
                              <TableCell>{row.sku}</TableCell>
                              <TableCell>
                                {row.shopping_buy_price ?? "—"}
                              </TableCell>
                              <TableCell>
                                {row.local_buy_price ?? "—"}
                              </TableCell>
                              <TableCell>
                                {row.shopping_datum
                                  ? new Date(
                                      row.shopping_datum,
                                    ).toLocaleString()
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {report.fba_stuck_update_needed?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      FBA stuck UPDATE_NEEDED (
                      {report.fba_stuck_update_needed_count})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>SKU</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Shopping datum</TableCell>
                            <TableCell>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.fba_stuck_update_needed.map((row) => (
                            <TableRow key={row.sku}>
                              <TableCell>{row.sku}</TableCell>
                              <TableCell>{row.status ?? "—"}</TableCell>
                              <TableCell>
                                {row.shopping_datum
                                  ? new Date(
                                      row.shopping_datum,
                                    ).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell>{row.error_message ?? "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {report.stock_mismatch?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Stock / status mismatch ({report.stock_mismatch_count})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>SKU</TableCell>
                            <TableCell>Channel</TableCell>
                            <TableCell>Seller SKU</TableCell>
                            <TableCell>DB stock</TableCell>
                            <TableCell>Platform stock</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.stock_mismatch.map((row, idx) => (
                            <TableRow
                              key={`${row.sku}-${row.channel}-${row.seller_sku || idx}`}
                            >
                              <TableCell>{row.sku}</TableCell>
                              <TableCell>{row.channel}</TableCell>
                              <TableCell>{row.seller_sku ?? "—"}</TableCell>
                              <TableCell>{row.db_stock ?? "—"}</TableCell>
                              <TableCell>{row.platform_stock ?? "—"}</TableCell>
                              <TableCell>{row.detail_status ?? "—"}</TableCell>
                              <TableCell>{row.reason ?? "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {report.external_bound_mismatch?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      External API bound mismatch (
                      {report.external_bound_mismatch_count})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>SKU</TableCell>
                            <TableCell>Channel</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell>Bound</TableCell>
                            <TableCell>Dashboard</TableCell>
                            <TableCell>Marketplace API</TableCell>
                            <TableCell>Source</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.external_bound_mismatch.map((row, idx) => (
                            <TableRow
                              key={`${row.sku}-${row.channel}-${row.bound}-${idx}`}
                            >
                              <TableCell>{row.sku}</TableCell>
                              <TableCell>{row.channel}</TableCell>
                              <TableCell>{row.region ?? "—"}</TableCell>
                              <TableCell>{row.bound}</TableCell>
                              <TableCell>{row.db_value ?? "—"}</TableCell>
                              <TableCell>{row.external_value ?? "—"}</TableCell>
                              <TableCell>
                                {row.external_source ?? "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={runAudit} disabled={running} variant="outlined">
          {running ? "Running..." : "Run now"}
        </Button>
        <Button onClick={saveConfig} disabled={saving} variant="contained">
          {saving ? "Saving..." : "Save config"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- UVP settings dialog --------------------------------------------------------

function UvpDialog({ article, open, onClose, onSaved }) {
  const [minUvp, setMinUvp] = useState("");
  const [maxUvp, setMaxUvp] = useState("");
  const [opts, setOpts] = useState({
    update_shopify: true,
    update_fbm: true,
    update_kaufland: true,
    update_otto: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setMinUvp(article?.min_uvp ? String(article.min_uvp) : "");
      setMaxUvp(article?.max_uvp ? String(article.max_uvp) : "");
      setError(null);
    }
  }, [open, article]);

  const handleSave = async () => {
    const minVal = Number(minUvp);
    const maxVal = Number(maxUvp);

    if (
      (minUvp && isNaN(minVal)) ||
      (maxUvp && isNaN(maxVal)) ||
      (!minUvp && !maxUvp)
    ) {
      setError("Please enter a valid number for at least one UVP bound.");
      return;
    }

    if (minUvp && maxUvp && minVal > maxVal) {
      setError("Min UVP cannot be greater than Max UVP.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await RepricerArticlesApi.setUVP(article.sku, {
        min_uvp: minUvp ? minVal : null,
        max_uvp: maxUvp ? maxVal : null,
        ...opts,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError(null);
    try {
      await RepricerArticlesApi.removeUVP(article.sku, {
        remove_shopify: opts.update_shopify,
        remove_fbm: opts.update_fbm,
        remove_kaufland: opts.update_kaufland,
        remove_otto: opts.update_otto,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Dialog
      PaperComponent={DialogDraggablePaper}
      aria-labelledby="draggable-uvp-title"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="draggable-uvp-title" style={{ cursor: "move" }}>
        UVP Settings: {article?.sku}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>
          Setting a UVP will manually override dynamic pricing calculation
          boundaries. The specified channels will have their prices locked
          within the specified UVP bounds until the UVP rule is removed.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              label="Min UVP (€)"
              value={minUvp}
              onChange={(e) => setMinUvp(e.target.value)}
              type="number"
              size="small"
              fullWidth
              inputProps={{ step: "any" }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Max UVP (€)"
              value={maxUvp}
              onChange={(e) => setMaxUvp(e.target.value)}
              type="number"
              size="small"
              fullWidth
              inputProps={{ step: "any" }}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" gutterBottom>
          Channels to override
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={opts.update_shopify}
                  onChange={(e) =>
                    setOpts({ ...opts, update_shopify: e.target.checked })
                  }
                />
              }
              label="Shopify"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={opts.update_kaufland}
                  onChange={(e) =>
                    setOpts({ ...opts, update_kaufland: e.target.checked })
                  }
                />
              }
              label="Kaufland"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={opts.update_otto}
                  onChange={(e) =>
                    setOpts({ ...opts, update_otto: e.target.checked })
                  }
                />
              }
              label="Otto"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={opts.update_fbm}
                  onChange={(e) =>
                    setOpts({ ...opts, update_fbm: e.target.checked })
                  }
                />
              }
              label="FBM"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {(article?.min_uvp != null || article?.max_uvp != null) && (
          <Button color="error" onClick={handleRemove} disabled={saving}>
            Remove UVP
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save UVP
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Margin rules dialog --------------------------------------------------------

function MarginRuleDialog({ article, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !article) {
      setRules({});
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const fetchRules = async () => {
      try {
        const ottoId = article.otto?.margin_rule_id;
        const kauflandId = article.kaufland?.margin_rule_id;
        const fbmId = pickPrimaryFbm(article.fbm, article.sku)?.margin_rule_id;
        const shopifyId = article.shopify?.margin_rule_id;

        const results = {};
        const ottoLikeIds = [
          ...new Set([ottoId, kauflandId, fbmId].filter((id) => id != null)),
        ];

        for (const id of ottoLikeIds) {
          try {
            const res = await OttoMarginRulesApi.getMarginRule(id);
            results[`otto-${id}`] = res.data;
          } catch (e) {
            console.error(`Failed to load Otto-like margin rule ${id}`, e);
          }
        }

        if (shopifyId != null) {
          try {
            const res = await ShopifyApi.getMarginRule(shopifyId);
            results[`shopify-${shopifyId}`] = res.data;
          } catch (e) {
            console.error(`Failed to load Shopify margin rule ${shopifyId}`, e);
          }
        }

        if (active) {
          setRules({
            kaufland: kauflandId ? results[`otto-${kauflandId}`] : null,
            otto: ottoId ? results[`otto-${ottoId}`] : null,
            fbm: fbmId ? results[`otto-${fbmId}`] : null,
            shopify: shopifyId ? results[`shopify-${shopifyId}`] : null,
          });
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRules();

    return () => {
      active = false;
    };
  }, [open, article]);

  const renderOttoLikeRule = (rule) => {
    if (!rule)
      return (
        <Typography variant="caption" color="text.secondary">
          Not found or not configured
        </Typography>
      );
    return (
      <Box sx={{ p: 1, bgcolor: "grey.100", borderRadius: 1, my: 0.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Min Margin:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {rule.min_margin} %
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Min Profit (Min):
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {fmt(rule.min_min_profit)} €
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Max Margin:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {rule.max_margin} %
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Max Profit (Min):
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {fmt(rule.max_min_profit)} €
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderShopifyRule = (rule) => {
    if (!rule)
      return (
        <Typography variant="caption" color="text.secondary">
          Not found or not configured
        </Typography>
      );
    return (
      <Box sx={{ p: 1, bgcolor: "grey.100", borderRadius: 1, my: 0.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Target Margin:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {rule.target_margin} %
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Min Profit:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {fmt(rule.min_profit)} €
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const primaryFbm = pickPrimaryFbm(article?.fbm, article?.sku);

  return (
    <Dialog
      PaperComponent={DialogDraggablePaper}
      aria-labelledby="draggable-margin-title"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="draggable-margin-title" style={{ cursor: "move" }}>
        Margin Rules: {article?.sku}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {article?.kaufland && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#e10915" }}>
                  Kaufland (ID: {article.kaufland.margin_rule_id || "None"})
                </Typography>
                {renderOttoLikeRule(rules.kaufland)}
              </Box>
            )}
            {article?.shopify && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#96bf48" }}>
                  Shopify (ID: {article.shopify.margin_rule_id || "None"})
                </Typography>
                {renderShopifyRule(rules.shopify)}
              </Box>
            )}
            {article?.otto && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#f00020" }}>
                  Otto (ID: {article.otto.margin_rule_id || "None"})
                </Typography>
                {renderOttoLikeRule(rules.otto)}
              </Box>
            )}
            {primaryFbm && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#1976d2" }}>
                  FBM (ID: {primaryFbm.margin_rule_id || "None"})
                </Typography>
                {renderOttoLikeRule(rules.fbm)}
              </Box>
            )}
            {article?.fba && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#1976d2" }}>
                  FBA (ID: {article.fba.margin_rule_id || "None"})
                </Typography>
                {renderOttoLikeRule(rules.fba)}
              </Box>
            )}
            {!article?.kaufland &&
              !article?.shopify &&
              !article?.otto &&
              !primaryFbm &&
              !article?.fba && (
                <Typography variant="body2" color="text.secondary">
                  No platforms configured for this article.
                </Typography>
              )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// -- Main component ------------------------------------------------------------

export default function BaseArticles({
  onNavigateToPlatform,
  initialSku = null,
  onNavConsumed,
}) {
  const theme = useTheme();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [salesStatsSku, setSalesStatsSku] = useState(null);

  // search / filter
  const [skuSearch, setSkuSearch] = useState(initialSku || "");
  const [nameSearch, setNameSearch] = useState("");
  const [activeSkuSearch, setActiveSkuSearch] = useState(initialSku || "");
  const [activeNameSearch, setActiveNameSearch] = useState("");
  const [hasDetailsFilter, setHasDetailsFilter] = useState("ALL"); // ALL | shopify | kaufland | otto | fbm
  const [articleTypeFilter, setArticleTypeFilter] = useState("ALL"); // ALL | penner | renner | neutral
  const [searchTrigger, setSearchTrigger] = useState(0);

  // consume initialSku navigation
  useEffect(() => {
    if (initialSku) {
      setSkuSearch(initialSku);
      setActiveSkuSearch(initialSku);
      setPage(0);
      if (onNavConsumed) onNavConsumed();
    }
  }, [initialSku, onNavConsumed]);

  // advanced filters & sort
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState({ field: null, dir: null });

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // sales / profit stats window

  // expanded rows (sku set)
  const [expandedRows, setExpandedRows] = useState(new Set());

  // quick view
  const [quickViewSkus, setQuickViewSkus] = useState([]);

  const handleOpenQuickView = useCallback((sku) => {
    setQuickViewSkus((prev) => (prev.includes(sku) ? prev : [...prev, sku]));
  }, []);

  const handleCloseQuickView = useCallback((sku) => {
    setQuickViewSkus((prev) => prev.filter((s) => s !== sku));
  }, []);

  // dialogs
  const [editArticle, setEditArticle] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteSku, setDeleteSku] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uvpArticle, setUvpArticle] = useState(null);
  const [uvpOpen, setUvpOpen] = useState(false);
  const [marginRuleArticle, setMarginRuleArticle] = useState(null);
  const [marginRuleOpen, setMarginRuleOpen] = useState(false);

  // per-row recalc
  const [recalcLoading, setRecalcLoading] = useState({});

  // top-level sync
  const [syncing, setSyncing] = useState(false);
  const [syncUpdatePrices, setSyncUpdatePrices] = useState(true);

  // cron
  const [cronStatus, setCronStatus] = useState({
    active: false,
    expression: "0 8 * * *",
    last_run_at: null,
    next_run_at: null,
  });
  const [cronDialogOpen, setCronDialogOpen] = useState(false);
  const [cronExpr, setCronExpr] = useState("0 8 * * *");
  const [cronUpdatePrices, setCronUpdatePrices] = useState(true);
  const [cronDiscordWebhook, setCronDiscordWebhook] = useState("");
  const [cronLoading, setCronLoading] = useState(false);

  // health audit config + dialog
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthConfig, setHealthConfig] = useState(null);
  const [healthLastReport, setHealthLastReport] = useState(null);

  const buildFilters = useCallback(() => {
    const f = [];
    if (activeSkuSearch)
      f.push({ field: "sku", op: "like", value: `%${activeSkuSearch}%` });
    if (activeNameSearch)
      f.push({
        field: "articlename",
        op: "like",
        value: `%${activeNameSearch}%`,
      });
    if (hasDetailsFilter !== "ALL")
      f.push({ field: `${hasDetailsFilter}.article_id`, op: "is_not_null" });
    if (articleTypeFilter !== "ALL")
      f.push({ field: "article_type", op: "eq", value: articleTypeFilter });

    // Inject active advanced filters
    for (const af of activeAdvancedFilters) {
      if (af.field && af.op) {
        if (af.op === "is_null" || af.op === "is_not_null") {
          f.push({ field: af.field, op: af.op });
        } else if (af.value !== "") {
          if (
            af.field === "manufacturer" &&
            (af.op === "in" || af.op === "nin")
          ) {
            const values = af.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((s) => s.toLowerCase());
            if (values.length) {
              f.push({ field: af.field, op: af.op, value: values });
            }
          } else {
            f.push({ field: af.field, op: af.op, value: af.value });
          }
        }
      }
    }
    return f.length ? f : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSkuSearch,
    activeNameSearch,
    hasDetailsFilter,
    articleTypeFilter,
    searchTrigger,
    activeAdvancedFilters,
  ]);

  const loadArticles = useCallback(
    async (force = false) => {
      const filters = buildFilters();
      const orders = sortOrder.field
        ? [{ field: sortOrder.field, dir: sortOrder.dir }]
        : undefined;
      const cacheKey = `multi_articles_${JSON.stringify({
        filters,
        orders,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })}`;
      if (!force) {
        const cached = getCached(cacheKey);
        if (cached) {
          setArticles(cached.articles);
          setTotal(cached.total);
          return;
        }
      }
      setLoading(true);
      setError(null);
      try {
        const res = await RepricerArticlesApi.getArticles({
          filters,
          orders,
          details: "all",
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        });
        const data = res.data || [];
        setArticles(data);
        setTotal(data.length);
        setCached(cacheKey, { articles: data, total: data.length });
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    },
    [buildFilters, sortOrder, page, rowsPerPage],
  );

  const loadStats = useCallback(async (force = false) => {
    const cacheKey = "multi_articles_stats";
    if (!force) {
      const cached = getCached(cacheKey);
      if (cached) {
        setStats(cached);
        return;
      }
    }
    try {
      const res = await RepricerArticlesApi.getArticleStats();
      setStats(res.data);
      setCached(cacheKey, res.data);
    } catch {
      /* silent */
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    try {
      const res = await RepricerArticlesApi.getCronStatus();
      setCronStatus(res.data);
      if (res.data.expression) setCronExpr(res.data.expression);
      if (res.data.arguments) {
        if (res.data.arguments.update_prices !== undefined) {
          setCronUpdatePrices(res.data.arguments.update_prices);
        }
        if (res.data.arguments.discord_webhook !== undefined) {
          setCronDiscordWebhook(res.data.arguments.discord_webhook || "");
        }
      }
    } catch {
      /* silent */
    }
  }, []);

  const loadHealthStatus = useCallback(async () => {
    try {
      const res = await HealthApi.getCronStatus();
      const args = res.data?.arguments || {};
      setHealthConfig({
        days: args.days ?? 3,
        max_hours_after_base_run: args.max_hours_after_base_run ?? 48,
        discord_webhook: args.discord_webhook || "",
        notify_on_nonzero: args.notify_on_nonzero ?? true,
      });
      if (res.data?.last_report) setHealthLastReport(res.data.last_report);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);
  useEffect(() => {
    loadStats();
    loadCronStatus();
    loadHealthStatus();
  }, [loadStats, loadCronStatus, loadHealthStatus]);

  const handleSearch = () => {
    setPage(0);
    setActiveSkuSearch(skuSearch);
    setActiveNameSearch(nameSearch);
    setActiveAdvancedFilters([...advancedFilters]);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSkuSearch("");
    setNameSearch("");
    setActiveSkuSearch("");
    setActiveNameSearch("");
    setAdvancedFilters([]);
    setActiveAdvancedFilters([]);
    setHasDetailsFilter("ALL");
    setArticleTypeFilter("ALL");
    setSortOrder({ field: null, dir: null });
    setPage(0);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleSort = (field) => {
    setSortOrder((prev) => {
      const isAsc = prev.field === field && prev.dir === "asc";
      const isDesc = prev.field === field && prev.dir === "desc";
      if (isAsc) return { field, dir: "desc" };
      if (isDesc) return { field: null, dir: null };
      return { field, dir: "asc" };
    });
    setPage(0);
  };

  const handleAddAdvancedFilter = () => {
    setAdvancedFilters((prev) => [
      ...prev,
      { id: Date.now(), field: "brutto_buy_price", op: "eq", value: "" },
    ]);
  };

  const handleRemoveAdvancedFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdateAdvancedFilter = (id, key, val) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)),
    );
  };

  const toggleRow = (sku) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await RepricerArticlesApi.syncArticles("all", syncUpdatePrices);
      setSuccess("Sync triggered successfully");
      await loadArticles(true);
      await loadStats(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncArticle = async (sku) => {
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await RepricerArticlesApi.syncArticles(sku);
      setSuccess(`Synced: ${sku}`);
      await loadArticles(true);
    } catch (e) {
      setError(
        `Sync failed for ${sku}: ${e.response?.data?.detail || e.message}`,
      );
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleRecalculate = async (sku, article) => {
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    const errors = [];
    try {
      await RepricerArticlesApi.syncArticles(sku).catch((e) =>
        errors.push(`Sync: ${e.response?.data?.detail || e.message}`),
      );
      if (errors.length) {
        setError(`Partial errors: ${errors.join(", ")}`);
      } else {
        setSuccess(`Recalculated: ${sku}`);
      }
      await loadArticles(true);
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleResetPennerRenner = async (sku, article) => {
    const typeLabel = (article?.article_type || "penner/renner").toUpperCase();
    if (
      !window.confirm(
        `Reset ${typeLabel} for ${sku}?\n\nThis clears article_type and pricing_history, sets use_min_max_rules=true on all channel details, and recalculates prices.`,
      )
    ) {
      return;
    }
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await RepricerArticlesApi.resetPennerRenner(sku);
      setSuccess(`Reset penner/renner and recalculated: ${sku}`);
      await loadArticles(true);
    } catch (e) {
      setError(
        `Reset failed for ${sku}: ${e.response?.data?.detail || e.message}`,
      );
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleStartCron = async () => {
    setCronLoading(true);
    try {
      await RepricerArticlesApi.startCronJob(
        cronExpr,
        cronUpdatePrices,
        cronDiscordWebhook || null,
      );
      setSuccess(`Cron started: ${cronExpr}`);
      setCronDialogOpen(false);
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  const handleStopCron = async () => {
    setCronLoading(true);
    try {
      await RepricerArticlesApi.stopCronJob();
      setSuccess("Cron stopped");
      await loadCronStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCronLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <StatsBar stats={stats} />

          {/* Action bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={
                syncing ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={handleSync}
              disabled={syncing}
              size="small"
            >
              Sync All
            </Button>
            <Tooltip title="If checked, this will force update prices on all platforms during the sync process.">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={syncUpdatePrices}
                    onChange={(e) => setSyncUpdatePrices(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Update Prices</Typography>}
                sx={{ m: 0 }}
              />
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadArticles(true);
                loadStats(true);
              }}
              size="small"
            >
              Refresh
            </Button>

            <Divider orientation="vertical" flexItem />

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={
                  cronStatus.active
                    ? `Cron: ON (${cronStatus.expression || ""})`
                    : "Cron: OFF"
                }
                color={cronStatus.active ? "success" : "default"}
                variant="outlined"
              />
              {cronStatus.active ? (
                <Tooltip title="Stop cron">
                  <IconButton
                    size="small"
                    onClick={handleStopCron}
                    disabled={cronLoading}
                    color="error"
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Configure & start cron">
                  <IconButton
                    size="small"
                    onClick={() => setCronDialogOpen(true)}
                    color="success"
                  >
                    <PlayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {cronStatus.next_run_at && (
              <Typography variant="caption" color="text.secondary">
                Next: {new Date(cronStatus.next_run_at).toLocaleString()}
              </Typography>
            )}

            <Divider orientation="vertical" flexItem />

            <Tooltip title="Configure & run health audit (buy-price freshness)">
              <Button
                size="small"
                variant="outlined"
                startIcon={<HealthIcon fontSize="small" />}
                onClick={() => setHealthDialogOpen(true)}
              >
                Health
              </Button>
            </Tooltip>
          </Stack>

          {/* Filter bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: skuSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSkuSearch("");
                      setActiveSkuSearch("");
                      setPage(0);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                ),
              }}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              placeholder="Search Name"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: nameSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setNameSearch("");
                      setActiveNameSearch("");
                      setPage(0);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                ),
              }}
              sx={{ width: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Has Details</InputLabel>
              <Select
                value={hasDetailsFilter}
                label="Has Details"
                onChange={(e) => {
                  setHasDetailsFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="shopify">Shopify</MenuItem>
                <MenuItem value="kaufland">Kaufland</MenuItem>
                <MenuItem value="otto">Otto</MenuItem>
                <MenuItem value="fbm">FBM</MenuItem>
                <MenuItem value="fba">FBA</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Article Type</InputLabel>
              <Select
                value={articleTypeFilter}
                label="Article Type"
                onChange={(e) => {
                  setArticleTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="penner">Penner</MenuItem>
                <MenuItem value="renner">Renner</MenuItem>
                <MenuItem value="neutral">Neutral</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
            <Button
              variant={advancedFiltersOpen ? "contained" : "outlined"}
              size="small"
              onClick={() => setAdvancedFiltersOpen((prev) => !prev)}
              startIcon={<FilterListIcon />}
            >
              Filters ({advancedFilters.length})
            </Button>
            {(activeSkuSearch ||
              activeNameSearch ||
              hasDetailsFilter !== "ALL" ||
              articleTypeFilter !== "ALL" ||
              activeAdvancedFilters.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearSearch}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            )}
          </Stack>

          <Collapse in={advancedFiltersOpen} unmountOnExit>
            <Paper variant="outlined" sx={getFilterPanelSx(theme)}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle2">Advanced Filters</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdvancedFilter}
                >
                  Add Rule
                </Button>
              </Stack>
              {advancedFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No advanced filters active.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {advancedFilters.map((f) => (
                    <Stack
                      key={f.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={f.field}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "field",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="brutto_buy_price">
                            Buy Price
                          </MenuItem>
                          <MenuItem value="stock">Stock</MenuItem>
                          <MenuItem value="package_type">Package Type</MenuItem>
                          <MenuItem value="package_cost">Package Cost</MenuItem>
                          <MenuItem value="weight">Weight</MenuItem>
                          <MenuItem value="uvp">UVP</MenuItem>
                          <MenuItem value="manufacturer">Manufacturer</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={f.op}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "op",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="eq">Equals (=)</MenuItem>
                          <MenuItem value="gt">Greater (&gt;)</MenuItem>
                          <MenuItem value="gte">Greater eq (&gt;=)</MenuItem>
                          <MenuItem value="lt">Less (&lt;)</MenuItem>
                          <MenuItem value="lte">Less eq (&lt;=)</MenuItem>
                          <MenuItem value="like">Contains</MenuItem>
                          <MenuItem value="in">In (values)</MenuItem>
                          <MenuItem value="nin">Not in (values)</MenuItem>
                          <MenuItem value="is_null">Is null/none</MenuItem>
                          <MenuItem value="is_not_null">Is not null</MenuItem>
                        </Select>
                      </FormControl>
                      {f.op !== "is_null" && f.op !== "is_not_null" && (
                        <>
                          {f.field === "manufacturer" &&
                          (f.op === "in" || f.op === "nin") ? (
                            <Autocomplete
                              multiple
                              freeSolo
                              size="small"
                              options={[]}
                              value={(f.value || "")
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .map((s) => s.toLowerCase())}
                              onChange={(_, newValue) =>
                                handleUpdateAdvancedFilter(
                                  f.id,
                                  "value",
                                  newValue
                                    .map((v) =>
                                      v.toString().trim().toLowerCase(),
                                    )
                                    .filter(Boolean)
                                    .join(","),
                                )
                              }
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    key={`${option}-${index}`}
                                    label={option}
                                    size="small"
                                    variant="outlined"
                                    {...getTagProps({ index })}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Manufacturers"
                                  sx={{ width: 240 }}
                                />
                              )}
                            />
                          ) : (
                            <TextField
                              size="small"
                              placeholder="Value"
                              value={f.value}
                              onChange={(e) =>
                                handleUpdateAdvancedFilter(
                                  f.id,
                                  "value",
                                  e.target.value,
                                )
                              }
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleSearch()
                              }
                              sx={{ width: 140 }}
                            />
                          )}
                        </>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdvancedFilter(f.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      sx={{ mt: 1 }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Collapse>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={getTableHeadRowSx(theme)}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "sku"}
                      direction={
                        sortOrder.field === "sku" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("sku")}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "articlename"}
                      direction={
                        sortOrder.field === "articlename"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("articlename")}
                    >
                      Name & Components
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Plenty IDs</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "brutto_buy_price"}
                      direction={
                        sortOrder.field === "brutto_buy_price"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("brutto_buy_price")}
                    >
                      Buy Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortOrder.field === "stock"}
                      direction={
                        sortOrder.field === "stock" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("stock")}
                    >
                      Stock
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "package_type"}
                      direction={
                        sortOrder.field === "package_type"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("package_type")}
                    >
                      Package
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "article_type"}
                      direction={
                        sortOrder.field === "article_type"
                          ? sortOrder.dir
                          : "asc"
                      }
                      onClick={() => handleSort("article_type")}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Platforms</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No articles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((a) => {
                    const expanded = expandedRows.has(a.sku);
                    const childSkus = (a.child_skus || "")
                      .split(",")
                      .filter((s) => s.trim());
                    let bundlePkgs = [];
                    if (a.bundle_packages) {
                      try {
                        bundlePkgs =
                          typeof a.bundle_packages === "string"
                            ? JSON.parse(a.bundle_packages)
                            : a.bundle_packages;
                      } catch {}
                    }

                    return (
                      <React.Fragment key={a.sku}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            "& > td": expanded ? { borderBottom: "none" } : {},
                          }}
                          onClick={() => toggleRow(a.sku)}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {expanded ? (
                                <CollapseIcon fontSize="small" />
                              ) : (
                                <ExpandIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {a.plenty_article_id && a.plenty_variant_id ? (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                component="a"
                                href={`https://p69524.my.plentysystems.com/plenty/terra/item/my-item/${a.plenty_article_id}/${a.plenty_variant_id}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  textDecoration: "underline",
                                  color: "primary.main",
                                }}
                              >
                                {a.sku}
                              </Typography>
                            ) : (
                              <Typography variant="body2" fontWeight={500}>
                                {a.sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: 250,
                                mb:
                                  childSkus.length > 0 || bundlePkgs.length > 0
                                    ? 0.5
                                    : 0,
                              }}
                            >
                              {a.articlename || "-"}
                            </Typography>
                            {childSkus.length > 0 && (
                              <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={0.5}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {childSkus.map((s) => (
                                  <Chip
                                    key={s}
                                    label={s}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => handleOpenQuickView(s)}
                                    sx={{
                                      cursor: "pointer",
                                      height: 18,
                                      fontSize: "0.6rem",
                                    }}
                                  />
                                ))}
                              </Stack>
                            )}
                            {bundlePkgs.length > 0 && (
                              <Stack
                                direction="column"
                                gap={0.5}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ mt: 0.5 }}
                              >
                                {bundlePkgs.map((c) => (
                                  <Box
                                    key={c.sku}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      {c.quantity}x
                                    </Typography>
                                    <Chip
                                      label={c.sku}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      onClick={() => handleOpenQuickView(c.sku)}
                                      sx={{
                                        cursor: "pointer",
                                        height: 18,
                                        fontSize: "0.6rem",
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {a.plenty_article_id != null
                                ? `Art: ${a.plenty_article_id}`
                                : "-"}
                              {a.plenty_variant_id != null
                                ? ` / Var: ${a.plenty_variant_id}`
                                : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {a.brutto_buy_price != null
                                ? `${fmt(a.brutto_buy_price)} €`
                                : "-"}
                            </Typography>
                            {a.uvp != null && (
                              <Tooltip title="UVP is actively overriding dynamic pricing">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "warning.main",
                                    fontWeight: "bold",
                                  }}
                                >
                                  UVP: {fmt(a.uvp)} €
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {a.stock ?? "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {a.package_type || "-"}
                              {a.package_cost != null
                                ? ` (${fmt(a.package_cost)} €)`
                                : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {a.article_type ? (
                              <Chip
                                label={a.article_type.toUpperCase()}
                                size="small"
                                color={
                                  a.article_type.toLowerCase() === "penner"
                                    ? "error"
                                    : a.article_type.toLowerCase() === "renner"
                                      ? "success"
                                      : "default"
                                }
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: "bold",
                                }}
                              />
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <PlatformsIndicator
                              article={a}
                              onNavigate={onNavigateToPlatform}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Stack
                              direction="row"
                              spacing={0.25}
                              justifyContent="center"
                            >
                              <SalesStatsOpenButton
                                onClick={() => setSalesStatsSku(a.sku)}
                              />
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditArticle(a);
                                    setEditOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sync article">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleSyncArticle(a.sku)}
                                    disabled={!!recalcLoading[a.sku]}
                                  >
                                    {recalcLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Recalculate prices">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "purple" }}
                                    onClick={() => handleRecalculate(a.sku, a)}
                                    disabled={!!recalcLoading[a.sku]}
                                  >
                                    {recalcLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CalcIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {["penner", "renner"].includes(
                                (a.article_type || "").toLowerCase(),
                              ) ? (
                                <Tooltip title="Reset Penner/Renner">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() =>
                                        handleResetPennerRenner(a.sku, a)
                                      }
                                      disabled={!!recalcLoading[a.sku]}
                                    >
                                      {recalcLoading[a.sku] ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <RestartAltIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDeleteSku(a.sku);
                                    setDeleteOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="UVP Setup">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => {
                                    setUvpArticle(a);
                                    setUvpOpen(true);
                                  }}
                                >
                                  <LocalOfferIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Margin Rules">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => {
                                    setMarginRuleArticle(a);
                                    setMarginRuleOpen(true);
                                  }}
                                >
                                  <RuleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded details row */}
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            sx={{ p: 0, border: expanded ? undefined : "none" }}
                          >
                            <Collapse
                              in={expanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <ArticleExpandedDetails
                                article={a}
                                onOpenSku={handleOpenQuickView}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={stats?.total_count ?? total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      {quickViewSkus.map((sku) => (
        <SkuQuickViewDialog
          key={sku}
          open={true}
          sku={sku}
          onClose={() => handleCloseQuickView(sku)}
          onOpenSku={handleOpenQuickView}
        />
      ))}

      <EditArticleDialog
        article={editArticle}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          loadArticles(true);
          loadStats(true);
        }}
      />

      <DeleteConfirmDialog
        sku={deleteSku}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          loadArticles(true);
          loadStats(true);
          setSuccess(`Deleted: ${deleteSku}`);
        }}
      />

      <UvpDialog
        article={uvpArticle}
        open={uvpOpen}
        onClose={() => setUvpOpen(false)}
        onSaved={() => {
          loadArticles(true);
          setSuccess("UVP setting saved and background calculation triggered.");
        }}
      />

      <MarginRuleDialog
        article={marginRuleArticle}
        open={marginRuleOpen}
        onClose={() => setMarginRuleOpen(false)}
      />

      <CronDialog
        open={cronDialogOpen}
        value={cronExpr}
        onChange={(v) => setCronExpr(v)}
        onConfirm={handleStartCron}
        onClose={() => setCronDialogOpen(false)}
        updatePrices={cronUpdatePrices}
        onUpdatePricesChange={setCronUpdatePrices}
        discordWebhook={cronDiscordWebhook}
        onDiscordWebhookChange={setCronDiscordWebhook}
      />

      <HealthAuditDialog
        open={healthDialogOpen}
        onClose={() => setHealthDialogOpen(false)}
        initialConfig={healthConfig}
        initialReport={healthLastReport}
        onSaved={(data) => {
          setSuccess("Health audit config saved");
          const args = data?.arguments || {};
          setHealthConfig({
            days: args.days ?? 3,
            max_hours_after_base_run: args.max_hours_after_base_run ?? 48,
            discord_webhook: args.discord_webhook || "",
            notify_on_nonzero: args.notify_on_nonzero ?? true,
          });
          if (data?.last_report) setHealthLastReport(data.last_report);
          setHealthDialogOpen(false);
        }}
      />

      <ArticleSalesStatsDialog
        open={!!salesStatsSku}
        onClose={() => setSalesStatsSku(null)}
        sku={salesStatsSku}
        articlename={articles.find((x) => x.sku === salesStatsSku)?.articlename}
        channel="all"
      />
    </Box>
  );
}
